import { useSyncExternalStore } from 'react';

/**
 * Cross-surface optimistic overlay for admin-managed (external) labels.
 *
 * Managed labels are read from the AGG `confluence_externalLabels` query, which
 * is eventually consistent behind the gateway: right after a `tags_assignLabel`
 * /`tags_unassignLabel` mutation succeeds, an immediate re-read can still return
 * the pre-mutation list. Regular labels avoid this because they use an Apollo
 * `optimisticResponse` on the shared, normalized `Content` entity that every
 * surface observes; managed labels are a standalone root-level list on a
 * separate cache, so there is no shared entity to propagate the change.
 *
 * This module provides a tiny module-level store (keyed by `contentId`) that all
 * managed-label surfaces — the edit dialog/picker, the details-panel sidebar,
 * and the page footer — read from, so an apply/remove on ANY surface is
 * reflected on ALL of them immediately, then reconciled away once the server
 * read catches up. A module-level store (vs React context) is used deliberately
 * because these surfaces do not share a common provider ancestor.
 */

export type OptimisticManagedLabel = {
	id: string;
	name: string;
};

export type ManagedLabelsOverlay = {
	/** Labels optimistically applied but not yet present in the server read. */
	readonly applied: readonly OptimisticManagedLabel[];
	/** Label ids optimistically removed but still present in the server read. */
	readonly removedIds: readonly string[];
};

// Stable empty overlay so `getSnapshot` returns a referentially-stable value
// when a content id has no pending optimistic changes (required by
// useSyncExternalStore to avoid render loops).
const EMPTY_OVERLAY: ManagedLabelsOverlay = { applied: [], removedIds: [] };

const overlaysByContentId = new Map<string, ManagedLabelsOverlay>();
const listeners = new Set<() => void>();

function emit(): void {
	for (const listener of listeners) {
		listener();
	}
}

function subscribe(listener: () => void): () => void {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
}

function getOverlay(contentId: string): ManagedLabelsOverlay {
	return overlaysByContentId.get(contentId) ?? EMPTY_OVERLAY;
}

function setOverlay(contentId: string, overlay: ManagedLabelsOverlay): void {
	if (overlay.applied.length === 0 && overlay.removedIds.length === 0) {
		overlaysByContentId.delete(contentId);
	} else {
		overlaysByContentId.set(contentId, overlay);
	}
	emit();
}

/** Optimistically mark a managed label as applied for the given content. */
export function optimisticallyApplyManagedLabel(
	contentId: string,
	label: OptimisticManagedLabel,
): void {
	if (!contentId || !label.id) {
		return;
	}
	const prev = getOverlay(contentId);
	setOverlay(contentId, {
		applied: [...prev.applied.filter((l) => l.id !== label.id), label],
		removedIds: prev.removedIds.filter((id) => id !== label.id),
	});
}

/** Optimistically mark a managed label as removed for the given content. */
export function optimisticallyRemoveManagedLabel(contentId: string, labelId: string): void {
	if (!contentId || !labelId) {
		return;
	}
	const prev = getOverlay(contentId);
	setOverlay(contentId, {
		applied: prev.applied.filter((l) => l.id !== labelId),
		removedIds: prev.removedIds.includes(labelId) ? prev.removedIds : [...prev.removedIds, labelId],
	});
}

/** Roll back an optimistic apply (e.g. the assign mutation failed). */
export function rollbackApplyManagedLabel(contentId: string, labelId: string): void {
	if (!contentId || !labelId) {
		return;
	}
	const prev = getOverlay(contentId);
	setOverlay(contentId, {
		applied: prev.applied.filter((l) => l.id !== labelId),
		removedIds: prev.removedIds,
	});
}

/** Roll back an optimistic removal (e.g. the unassign mutation failed). */
export function rollbackRemoveManagedLabel(contentId: string, labelId: string): void {
	if (!contentId || !labelId) {
		return;
	}
	const prev = getOverlay(contentId);
	setOverlay(contentId, {
		applied: prev.applied,
		removedIds: prev.removedIds.filter((id) => id !== labelId),
	});
}

/**
 * Reconcile the overlay against the authoritative server read: drop applied
 * overlays once the server includes them, and drop removed overlays once the
 * server no longer includes them. No-ops (with no state change / no emit) when
 * nothing needs pruning.
 */
export function reconcileManagedLabelsOverlay(
	contentId: string,
	serverIds: ReadonlySet<string>,
): void {
	if (!contentId) {
		return;
	}
	const prev = getOverlay(contentId);
	if (prev === EMPTY_OVERLAY) {
		return;
	}
	const applied = prev.applied.filter((l) => !serverIds.has(l.id));
	const removedIds = prev.removedIds.filter((id) => serverIds.has(id));
	if (applied.length === prev.applied.length && removedIds.length === prev.removedIds.length) {
		return;
	}
	setOverlay(contentId, { applied, removedIds });
}

/**
 * Merge a server-read managed-label list with the optimistic overlay, returning
 * a clean `{ id, name }[]` (managed-first order preserved from the server list,
 * with optimistically-applied labels appended). Optimistically-removed ids are
 * filtered out.
 */
export function mergeManagedLabelsWithOverlay<
	T extends { id?: string | null; name?: string | null },
>(serverLabels: readonly T[], overlay: ManagedLabelsOverlay): OptimisticManagedLabel[] {
	const byId = new Map<string, OptimisticManagedLabel>();
	for (const label of serverLabels) {
		if (label?.id && label?.name) {
			byId.set(label.id, { id: label.id, name: label.name });
		}
	}
	for (const label of overlay.applied) {
		byId.set(label.id, label);
	}
	for (const id of overlay.removedIds) {
		byId.delete(id);
	}
	return Array.from(byId.values());
}

/**
 * Test-only: clear all optimistic overlays so module-level state does not leak
 * between tests. Not for production use.
 */
export function __resetManagedLabelsOverlayForTests(): void {
	overlaysByContentId.clear();
	emit();
}

/** Subscribe to the optimistic overlay for a piece of content. */
export function useManagedLabelsOverlay(contentId: string): ManagedLabelsOverlay {
	return useSyncExternalStore(
		subscribe,
		() => getOverlay(contentId),
		// Server snapshot: no optimistic overlay exists during SSR.
		() => EMPTY_OVERLAY,
	);
}
